import React, { useState, useEffect } from 'react';
import { Modal, Button, Tabs, Tab, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { API_URL } from '../../config';
import { useAppData } from './AppDataContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark,
    faPlus,
    faSearch,
    faCheck,
    faWandMagicSparkles,
    faImage,
} from '@fortawesome/free-solid-svg-icons';

/**
 * MediaLibrary
 *
 * Props:
 *  - onSelectImage {Function}  Called with the URL when an image is confirmed
 *  - selectedUrl   {string}    Optional — the currently selected/active image URL.
 *                              When provided, that image is highlighted with a checkmark
 *                              and the modal shows a "Use This Image" confirm button
 *                              instead of closing immediately on click.
 *  - triggerLabel  {string}    Optional — label for the trigger element (default: "Change Media")
 */
const MediaLibrary = ({ onSelectImage, selectedUrl = null, triggerLabel = 'Change Media' }) => {
    const [mediaItems, setMediaItems] = useState([]);
    const [premiumMediaItems, setPremiumMediaItems] = useState([]);
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [premiumLoading, setPremiumLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('media');
    const [searchQuery, setSearchQuery] = useState('');

    // AI Generate tab state
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiGenerating, setAiGenerating] = useState(false);
    const [aiGeneratedUrl, setAiGeneratedUrl] = useState(null);
    const [aiError, setAiError] = useState(null);
    const [aiStyle, setAiStyle] = useState('photorealistic');

    // The image the user has clicked/highlighted inside the modal (not yet confirmed)
    const [pendingUrl, setPendingUrl] = useState(null);

    const { subdomain } = useAppData();

    // When modal opens, pre-select the currently active image
    useEffect(() => {
        if (show) {
            setPendingUrl(selectedUrl || null);
            // Reset AI tab state on open
            setAiPrompt('');
            setAiGeneratedUrl(null);
            setAiError(null);
        }
    }, [show, selectedUrl]);

    const fetchMediaItems = async () => {
        try {
            const response = await fetch(`${API_URL}/media?account_key=${subdomain}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setMediaItems(Array.isArray(data.media) ? data.media : []);
        } catch (error) {
            console.error('Failed to fetch media:', error);
            setMediaItems([]);
        }
    };

    const fetchPremiumMediaItems = async (query = '') => {
        setPremiumLoading(true);
        try {
            const url = query
                ? `${API_URL}/media/premium?account_key=${subdomain}&search=${encodeURIComponent(query)}`
                : `${API_URL}/media/premium?account_key=${subdomain}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setPremiumMediaItems(Array.isArray(data.media) ? data.media : []);
        } catch (error) {
            console.error('Failed to fetch premium media:', error);
            setPremiumMediaItems([]);
        } finally {
            setPremiumLoading(false);
        }
    };

    const handleFileUpload = async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        const formData = new FormData();
        formData.append('file', acceptedFiles[0]);
        formData.append('account_key', subdomain);
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/media/upload`, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            if (data.success && data.media) {
                setMediaItems((prevItems) => [data.media, ...prevItems]);
                setActiveTab('media');
                setPendingUrl(data.media.url);
            } else {
                console.error('Upload failed:', data.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Failed to upload file:', error);
        } finally {
            setLoading(false);
        }
    };

    // ── AI Image Generation ──────────────────────────────────────
    const handleAiGenerate = async () => {
        if (!aiPrompt.trim()) return;
        setAiGenerating(true);
        setAiError(null);
        setAiGeneratedUrl(null);

        // Build a richer prompt with the selected style
        const styleHints = {
            photorealistic: 'photorealistic, high quality, professional photography',
            illustration:   'digital illustration, flat design, colorful, clean',
            '3d':           '3D render, modern, high quality, studio lighting',
            watercolor:     'watercolor painting, artistic, soft colors',
            minimalist:     'minimalist, clean, simple, white background',
            cinematic:      'cinematic, dramatic lighting, film photography style',
        };
        const fullPrompt = `${aiPrompt.trim()}. Style: ${styleHints[aiStyle] || aiStyle}. Suitable for a professional website.`;

        try {
            const response = await fetch(`${API_URL}/chat/generate-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    description: fullPrompt,
                    account_key: subdomain,
                }),
            });

            if (!response.ok) throw new Error(`Generation failed: ${response.status}`);
            const data = await response.json();

            if (data.url) {
                setAiGeneratedUrl(data.url);
                setPendingUrl(data.url);
                // Also add to media items list so it appears in Media Library tab
                setMediaItems((prev) => [{
                    id: data.media_id || Date.now(),
                    url: data.url,
                    title: data.title || 'AI Generated',
                }, ...prev]);
            } else {
                throw new Error('No URL returned');
            }
        } catch (err) {
            console.error('AI generation error:', err);
            setAiError('Image generation failed. Please try again or rephrase your description.');
        } finally {
            setAiGenerating(false);
        }
    };

    const handleAiKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAiGenerate();
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleFileUpload,
        accept: 'image/*',
        multiple: false,
    });

    useEffect(() => {
        fetchMediaItems();
    }, [subdomain]);

    useEffect(() => {
        if (activeTab === 'premium') {
            fetchPremiumMediaItems('');
        }
    }, [activeTab, subdomain]);

    const openMediaLibrary = () => {
        setShow(true);
        setActiveTab('media');
    };

    const handleImageClick = (url) => {
        if (selectedUrl !== null) {
            setPendingUrl(url);
        } else {
            onSelectImage(url);
            setShow(false);
        }
    };

    const handleConfirm = () => {
        if (pendingUrl) {
            onSelectImage(pendingUrl);
            setShow(false);
        }
    };

    const handleSearchChange = (e) => setSearchQuery(e.target.value);
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchPremiumMediaItems(searchQuery);
    };

    const isPickerMode = selectedUrl !== null;

    const renderImageGrid = (items) =>
        items.map((media) => {
            const isSelected = pendingUrl === media.url;
            return (
                <div
                    key={media.id}
                    onClick={() => handleImageClick(media.url)}
                    style={{
                        position: 'relative',
                        display: 'inline-block',
                        margin: '8px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: isSelected ? '3px solid #6366f1' : '2px solid #ddd',
                        boxShadow: isSelected ? '0 0 0 2px rgba(99,102,241,0.3)' : 'none',
                        transition: 'border-color 0.15s, box-shadow 0.15s',
                    }}
                >
                    <img
                        src={media.url}
                        alt={media.title}
                        style={{
                            display: 'block',
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                        }}
                    />
                    {isSelected && (
                        <div style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            background: '#6366f1',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 'bold',
                        }}>
                            <FontAwesomeIcon icon={faCheck} />
                        </div>
                    )}
                    {selectedUrl === media.url && pendingUrl !== media.url && (
                        <div style={{
                            position: 'absolute',
                            bottom: '0',
                            left: '0',
                            right: '0',
                            background: 'rgba(0,0,0,0.55)',
                            color: '#fff',
                            fontSize: '10px',
                            textAlign: 'center',
                            padding: '2px 0',
                        }}>
                            Current
                        </div>
                    )}
                </div>
            );
        });

    // Style options for AI generation
    const styleOptions = [
        { value: 'photorealistic', label: '📷 Photorealistic' },
        { value: 'illustration',   label: '🎨 Illustration' },
        { value: '3d',             label: '🧊 3D Render' },
        { value: 'watercolor',     label: '🖌️ Watercolor' },
        { value: 'minimalist',     label: '⬜ Minimalist' },
        { value: 'cinematic',      label: '🎬 Cinematic' },
    ];

    // Prompt suggestions
    const promptSuggestions = [
        'Modern office interior with natural light',
        'Professional team working together',
        'Product on clean white background',
        'Abstract technology background',
        'Happy customer using a smartphone',
        'Beautiful landscape at golden hour',
    ];

    return (
        <>
            <div onClick={openMediaLibrary} className="chg-media-img" style={{ cursor: 'pointer' }}>
                {triggerLabel}
            </div>

            <Modal className="model-styling" show={show} onHide={() => setShow(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Media Library</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* Selected image preview bar */}
                    {isPickerMode && pendingUrl && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 14px',
                            background: '#f5f3ff',
                            border: '1px solid #c4b5fd',
                            borderRadius: '8px',
                            marginBottom: '14px',
                        }}>
                            <img
                                src={pendingUrl}
                                alt="Selected"
                                style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #6366f1' }}
                            />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#4f46e5' }}>
                                    {pendingUrl === selectedUrl ? 'Current image (click another to change)' : 'New image selected'}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', wordBreak: 'break-all' }}>
                                    {pendingUrl}
                                </div>
                            </div>
                        </div>
                    )}

                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>

                        {/* ── Tab 1: Media Library ─────────────────── */}
                        <Tab eventKey="media" title="Media Library">
                            <div className="media-library mt-3 d-flex flex-wrap">
                                {Array.isArray(mediaItems) && mediaItems.length > 0 ? (
                                    renderImageGrid(mediaItems)
                                ) : (
                                    <p>No media available.</p>
                                )}
                            </div>
                        </Tab>

                        {/* ── Tab 2: Upload Media ──────────────────── */}
                        <Tab eventKey="upload" title="Upload Media">
                            <div className="mt-3 upload-img-pop" {...getRootProps()}>
                                <FontAwesomeIcon icon={faPlus} />{' '}
                                {loading ? 'Uploading...' : 'Drag & drop an image here, or click to select one'}
                                <input {...getInputProps()} />
                            </div>
                        </Tab>

                        {/* ── Tab 3: Premium Media ─────────────────── */}
                        <Tab eventKey="premium" title="Premium Media">
                            <div className="mt-3">
                                <Form onSubmit={handleSearchSubmit}>
                                    <InputGroup className="mb-3">
                                        <Form.Control
                                            type="text"
                                            placeholder="Search premium media by keyword..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                        />
                                        <Button variant="outline-secondary" type="submit">
                                            <FontAwesomeIcon icon={faSearch} />
                                        </Button>
                                    </InputGroup>
                                </Form>
                                {premiumLoading ? (
                                    <p>Loading...</p>
                                ) : (
                                    <div className="premium-media-library d-flex flex-wrap">
                                        {Array.isArray(premiumMediaItems) && premiumMediaItems.length > 0 ? (
                                            renderImageGrid(premiumMediaItems)
                                        ) : (
                                            <p>No premium media found.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Tab>

                        {/* ── Tab 4: AI Generate ───────────────────── */}
                        <Tab
                            eventKey="ai"
                            title={
                                <span>
                                    <FontAwesomeIcon icon={faWandMagicSparkles} className="me-1" />
                                    AI Generate
                                </span>
                            }
                        >
                            <div className="mt-3">

                                {/* Style selector */}
                                <div className="mb-3">
                                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                        Image Style
                                    </Form.Label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {styleOptions.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => setAiStyle(opt.value)}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '20px',
                                                    border: aiStyle === opt.value ? '2px solid #6366f1' : '1.5px solid #e5e7eb',
                                                    background: aiStyle === opt.value ? '#f5f3ff' : '#fff',
                                                    color: aiStyle === opt.value ? '#4f46e5' : '#6b7280',
                                                    fontSize: '12px',
                                                    fontWeight: aiStyle === opt.value ? '600' : '400',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Prompt input */}
                                <div className="mb-3">
                                    <Form.Label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                                        Describe the image you want
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            placeholder="e.g. A professional team meeting in a modern office with large windows and natural light"
                                            value={aiPrompt}
                                            onChange={(e) => setAiPrompt(e.target.value)}
                                            onKeyDown={handleAiKeyDown}
                                            disabled={aiGenerating}
                                            style={{ resize: 'none', fontSize: '13px' }}
                                        />
                                    </InputGroup>
                                    <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                        Be specific for better results. Press Enter or click Generate.
                                    </Form.Text>
                                </div>

                                {/* Prompt suggestions */}
                                {!aiPrompt && (
                                    <div className="mb-3">
                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>
                                            Quick suggestions:
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {promptSuggestions.map((s) => (
                                                <button
                                                    key={s}
                                                    type="button"
                                                    onClick={() => setAiPrompt(s)}
                                                    style={{
                                                        padding: '4px 10px',
                                                        borderRadius: '12px',
                                                        border: '1px solid #e5e7eb',
                                                        background: '#f9fafb',
                                                        color: '#6b7280',
                                                        fontSize: '11.5px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.borderColor = '#6366f1';
                                                        e.target.style.color = '#6366f1';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.borderColor = '#e5e7eb';
                                                        e.target.style.color = '#6b7280';
                                                    }}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Generate button */}
                                <Button
                                    variant="primary"
                                    onClick={handleAiGenerate}
                                    disabled={aiGenerating || !aiPrompt.trim()}
                                    style={{
                                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                        border: 'none',
                                        padding: '9px 22px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    {aiGenerating ? (
                                        <>
                                            <Spinner animation="border" size="sm" />
                                            Generating... (this takes ~10s)
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                                            Generate Image
                                        </>
                                    )}
                                </Button>

                                {/* Error */}
                                {aiError && (
                                    <div style={{
                                        marginTop: '12px',
                                        padding: '10px 14px',
                                        background: '#fef2f2',
                                        border: '1px solid #fecaca',
                                        borderRadius: '8px',
                                        color: '#dc2626',
                                        fontSize: '13px',
                                    }}>
                                        {aiError}
                                    </div>
                                )}

                                {/* Generated image result */}
                                {aiGeneratedUrl && (
                                    <div style={{ marginTop: '20px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                                            <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981', marginRight: '6px' }} />
                                            Image generated and saved to your Media Library
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                                            <img
                                                src={aiGeneratedUrl}
                                                alt="AI Generated"
                                                style={{
                                                    width: '200px',
                                                    height: '200px',
                                                    objectFit: 'cover',
                                                    borderRadius: '10px',
                                                    border: pendingUrl === aiGeneratedUrl ? '3px solid #6366f1' : '2px solid #e5e7eb',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => handleImageClick(aiGeneratedUrl)}
                                            />
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <Button
                                                    size="sm"
                                                    variant={pendingUrl === aiGeneratedUrl ? 'success' : 'outline-primary'}
                                                    onClick={() => handleImageClick(aiGeneratedUrl)}
                                                    style={{ fontSize: '12px' }}
                                                >
                                                    {pendingUrl === aiGeneratedUrl ? (
                                                        <><FontAwesomeIcon icon={faCheck} /> Selected</>
                                                    ) : (
                                                        <><FontAwesomeIcon icon={faImage} /> Use This Image</>
                                                    )}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => {
                                                        setAiGeneratedUrl(null);
                                                        setAiError(null);
                                                    }}
                                                    style={{ fontSize: '12px' }}
                                                >
                                                    Generate Another
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Tab>

                    </Tabs>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="danger" onClick={() => setShow(false)}>
                        <FontAwesomeIcon icon={faXmark} /> Close
                    </Button>

                    {/* Confirm button — only shown in picker mode */}
                    {isPickerMode && (
                        <Button
                            variant="success"
                            onClick={handleConfirm}
                            disabled={!pendingUrl}
                        >
                            <FontAwesomeIcon icon={faCheck} /> Use This Image
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MediaLibrary;
